import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { SrIcon } from "@/components/ui/SrIcon";
import { toast } from "sonner";
import { showUndoToast } from "@/lib/undo-toast";
import {
  FEATURE_FLAGS,
  FEATURE_FLAG_ROLLOUT_LEVELS,
  type FeatureFlagKey,
  type FeatureFlagRolloutLevel,
} from "@/lib/feature-flags";
import { useFeatureFlagLevels, useUpdateFeatureFlagLevel } from "@/hooks/use-feature-flag";

const FLAG_KEYS = Object.keys(FEATURE_FLAGS) as FeatureFlagKey[];

/**
 * VIB-1396: Funksjonsbrytere-panel, kun for superadmin (gates i
 * DriftSettings.tsx — denne komponenten antar tilgangssjekk er gjort).
 *
 * Rendres FRA registeret (`src/lib/feature-flags.ts`) + gjeldende
 * `system_config`-verdier, så et nytt flagg dukker automatisk opp her uten
 * UI-arbeid — legg det kun til i registeret.
 */
export function FeatureFlagsPanel() {
  const { data: levels, isLoading } = useFeatureFlagLevels();
  const updateLevel = useUpdateFeatureFlagLevel();

  const onError = (err: unknown) => toast.error(`Feil: ${(err as Error).message}`);
  const levelLabel = (level: FeatureFlagRolloutLevel) =>
    FEATURE_FLAG_ROLLOUT_LEVELS.find((l) => l.value === level)?.label ?? level;

  // VIB-2530: angre-toast i stedet for dialog; angre skriver forrige nivå
  // tilbake som ny hendelse i audit-loggen.
  const handleChange = (key: FeatureFlagKey, level: FeatureFlagRolloutLevel) => {
    const prev: FeatureFlagRolloutLevel = levels?.[key] ?? "off";
    const label = FEATURE_FLAGS[key].label;
    updateLevel.mutate(
      { key, level },
      {
        onSuccess: () =>
          showUndoToast(`${label}: nivå satt til «${levelLabel(level)}»`, () =>
            updateLevel.mutate(
              { key, level: prev },
              {
                onSuccess: () => toast.success(`${label}: tilbake til «${levelLabel(prev)}»`),
                onError,
              },
            ),
          ),
        onError,
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <SrIcon name="flag" className="h-4 w-4 text-muted-foreground" />
          Funksjonsbrytere
        </CardTitle>
        <CardDescription>
          Rollout-nivå styrer hvem som ser funksjonen: av → kun superadmin → admin og superadmin →
          alle. Hver endring logges med deg som aktør.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading && (
          <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
        )}
        {!isLoading && FLAG_KEYS.length === 0 && (
          <p className="text-sm text-muted-foreground">Ingen flagg registrert ennå.</p>
        )}
        {!isLoading &&
          FLAG_KEYS.map((key) => {
            const meta = FEATURE_FLAGS[key];
            const level = levels?.[key] ?? "off";
            return (
              <div
                key={key}
                className="flex flex-col gap-2 border-b border-border pb-4 last:border-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{meta.label}</span>
                    {meta.status === "reserved" && (
                      <Badge variant="outline" className="text-xs">
                        Reservert
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{meta.description}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Nøkkel: <code>{key}</code> · Eier: {meta.owner}
                  </p>
                </div>
                <Select
                  value={level}
                  onValueChange={(value) => handleChange(key, value as FeatureFlagRolloutLevel)}
                  disabled={updateLevel.isPending}
                >
                  <SelectTrigger className="w-full sm:w-[220px]" aria-label={`Rollout-nivå for ${meta.label}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FEATURE_FLAG_ROLLOUT_LEVELS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
      </CardContent>
    </Card>
  );
}
